import React from 'react';
const { useState, useEffect } = React;
import { F } from '../utils.jsx';
import { INTAKE_FIELDS } from './quotes/QuoteEditor.jsx';
import { openQuoteFile, recordTotalText } from './quotes/QuoteList.jsx';
import { fileKindOf } from '../quoteFile.js';

export default function AccountIntakeTab({
  account, intakeRecord, quoteRecord, intakeLoading, generatingIntake,
  loadIntakeRecord, generateIntakeLink,
}) {
  useEffect(() => {
    if (account && !intakeRecord && !intakeLoading) loadIntakeRecord();
  }, [account?.id]);

  const fields = intakeRecord?.fields || {};
  // The quote shown: the most recent open one (a follow-up created from a
  // Diagnostic has Source Diagnostic) or, without it, the intake's own quote
  const quote = quoteRecord || intakeRecord;
  const quoteFields = quote?.fields || {};
  const isFollowUp = !!quote && !!intakeRecord && quote.id !== intakeRecord.id;
  const token = fields['Token'] || '';
  const status = fields['Status'] || '';
  const submittedAt = fields['Submitted At'];
  const intakeUrl = token ? `${window.location.origin}/intake.html?token=${token}` : '';

  const copyLink = (lang = 'es') => {
    if (!intakeUrl) return;
    const url = lang === 'en' ? intakeUrl + '&lang=en' : intakeUrl;
    navigator.clipboard.writeText(url);
    window.__oikeToast(lang === 'en' ? 'Link (EN) copied' : 'Link (ES) copied', 'success');
  };

  const filledCount = INTAKE_FIELDS.filter(f => fields[f.key]?.trim?.()).length;


  if (intakeLoading) {
    return (
      <div style={{ padding: '40px 0', textAlign: 'center', color: 'var(--globant-muted)', fontSize: 13 }}>
        ⏳ Loading intake…
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

      {/* ── Header + link card ── */}
      <div className="card" style={{ borderLeft: '3px solid var(--globant-green)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <h3 style={{ marginBottom: 6 }}>Formulario previo del cliente</h3>
            <p style={{ fontSize: 12, color: 'var(--globant-muted)', margin: 0 }}>
              Generá un link único para que <strong style={{ color: 'var(--globant-text)' }}>{F(account, 'Account Name')}</strong> complete el prediagnóstico antes de la primera sesión.
            </p>
          </div>
          {!intakeRecord && (
            <button className="action-btn btn-primary" style={{ fontSize: 12, whiteSpace: 'nowrap' }}
              onClick={generateIntakeLink} disabled={generatingIntake}>
              {generatingIntake ? 'Generando…' : 'Generar link del formulario'}
            </button>
          )}
        </div>

        {intakeRecord && (
          <div style={{ marginTop: 16, padding: '14px 16px', background: 'var(--globant-darker)', borderRadius: 10, border: '1px solid var(--globant-border)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <span style={{
                fontSize: 10, fontWeight: 700, padding: '3px 10px', borderRadius: 20,
                background: status === 'Submitted' ? 'rgba(52,211,153,0.15)' : 'rgba(251,191,36,0.12)',
                color: status === 'Submitted' ? '#1E7B4F' : '#9A6212',
                border: `1px solid ${status === 'Submitted' ? 'rgba(52,211,153,0.3)' : 'rgba(251,191,36,0.25)'}`,
                whiteSpace: 'nowrap',
              }}>
                {status === 'Submitted' ? 'Completo' : 'Pendiente'}
              </span>
              {submittedAt && (
                <span style={{ fontSize: 11, color: 'var(--globant-muted)' }}>
                  · Enviado el {new Date(submittedAt).toLocaleDateString('es', { day: 'numeric', month: 'short', year: 'numeric' })}
                </span>
              )}
              {status === 'Submitted' && (
                <span style={{ fontSize: 11, color: 'var(--globant-muted)' }}>
                  · {filledCount}/{INTAKE_FIELDS.length} campos completos
                </span>
              )}
            </div>

            {status !== 'Submitted' && (
              <div style={{ marginTop: 12 }}>
                <div style={{ fontSize: 10, color: 'var(--globant-muted)', marginBottom: 6, fontWeight: 600, letterSpacing: '0.5px' }}>LINK PARA COMPARTIR</div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <div style={{ flex: 1, background: 'rgba(30,42,44,0.03)', border: '1px solid var(--globant-border)', borderRadius: 8, padding: '8px 12px', fontSize: 11, color: 'var(--globant-muted)', fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {intakeUrl}
                  </div>
                  <button className="action-btn btn-primary" style={{ fontSize: 11, whiteSpace: 'nowrap' }} onClick={() => copyLink('es')}>
                    📋 ES
                  </button>
                  <button className="action-btn btn-ghost" style={{ fontSize: 11, whiteSpace: 'nowrap' }} onClick={() => copyLink('en')}>
                    📋 EN
                  </button>
                </div>
                <p style={{ fontSize: 11, color: 'var(--globant-muted)', marginTop: 8, margin: '8px 0 0' }}>
                  Mandale este link al cliente por email o WhatsApp. No necesita cuenta.
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Client responses ── */}
      {intakeRecord && status === 'Submitted' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ fontSize: 10, fontWeight: 800, color: 'var(--globant-muted)', letterSpacing: '1px', textTransform: 'uppercase' }}>
            Client Responses
          </div>
          {INTAKE_FIELDS.map(({ key, show, icon }) => {
            const val = fields[key];
            if (!val) return null;
            return (
              <div key={key} className="card" style={{ padding: '14px 16px' }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--globant-muted)', letterSpacing: '0.8px', textTransform: 'uppercase', marginBottom: 8 }}>
                  {icon} {show}
                </div>
                <div style={{ fontSize: 13, color: 'var(--globant-text)', lineHeight: 1.65, whiteSpace: 'pre-wrap' }}>{val}</div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── The quote of this form: edited, sent and accepted only in its file (the same from every access) ── */}
      {quote && (
        <div className="card" style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 260px' }}>
            <div style={{ fontSize: 13, fontWeight: 700 }}>{fileKindOf(quoteFields).label}{isFollowUp ? ' de seguimiento' : ''} · {fileKindOf(quoteFields).state}</div>
            <div style={{ fontSize: 12, color: 'var(--globant-muted)' }}>{recordTotalText(quoteFields)}. Se arma, se publica y se registra su aceptación en su ficha, con estas respuestas a la vista.</div>
          </div>
          <button type="button" className="action-btn btn-primary" style={{ fontSize: 12 }} onClick={() => openQuoteFile(quote.id)}>Abrir ficha</button>
        </div>
      )}

      {!intakeRecord && !generatingIntake && (
        <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--globant-muted)', fontSize: 13 }}>
          <div style={{ fontSize: 32, marginBottom: 12 }}>📋</div>
          <div style={{ fontWeight: 600, marginBottom: 6, color: 'var(--globant-text)' }}>Todavía no hay formulario</div>
          <div>Generá el link y compartilo con el cliente para que complete el prediagnóstico.</div>
        </div>
      )}
    </div>
  );
}
